import React, { useState } from 'react'
import { InteractionPoint, InteractionPointStyles } from '@deepguide-ai/types'
import { cn } from '@/lib/utils'

interface InteractionPointComponentProps {
  point: InteractionPoint
  index: number
  onClick?: (index: number) => void
  className?: string
}

export const InteractionPointComponent: React.FC<
  InteractionPointComponentProps
> = ({ point, index, onClick, className }) => {
  const { x, y, label, type = 'Click', size = 'md', color, styles = {} } = point
  const [isHovered, setIsHovered] = useState(false)

  const sizeClasses = {
    sm: 'size-4',
    md: 'size-6',
    lg: 'size-8',
  }

  // Default type-based styling
  const typeClasses = {
    Click: 'animate-pulse bg-primary/50 border-primary',
    Hover: 'animate-none bg-blue-500/50 border-blue-500',
    Scroll: 'animate-bounce bg-green-500/50 border-green-500',
    Input: 'animate-none bg-yellow-500/50 border-yellow-500',
    Submit: 'animate-none bg-purple-500/50 border-purple-500',
    Custom: color
      ? `bg-${color}/50 border-${color}`
      : 'bg-orange-500/50 border-orange-500',
  }

  // Apply custom styles if provided
  const pointStyle = {
    left: `${x}%`,
    top: `${y}%`,
    backgroundColor: styles.backgroundColor,
    borderColor: styles.borderColor,
  }

  // Arrow positioning classes
  const arrowPositionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  }

  const arrowPosition = styles.arrowPosition || 'bottom'

  return (
    <div
      className={cn(
        'absolute z-10 -translate-x-1/2 -translate-y-1/2 rounded-full border-2',
        'transition-all duration-200 ease-in-out',
        sizeClasses[size],
        !styles.backgroundColor &&
          !styles.borderColor &&
          (typeClasses[type as keyof typeof typeClasses] ?? typeClasses.Custom),
        isHovered && 'scale-125 shadow-lg',
        styles.customClassName,
        className
      )}
      style={pointStyle}
      title={label || `Interaction point ${index + 1}`}
      onClick={() => onClick?.(index)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <span
        className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-xs font-bold'
        style={{ color: styles.textColor || 'white' }}
      >
        {index + 1}
      </span>

      {/* Show tooltip on hover */}
      {isHovered && label && (
        <div
          className={cn(
            'absolute z-20 rounded px-2 py-1 text-xs whitespace-nowrap',
            'min-w-[100px] text-center shadow-md',
            arrowPositionClasses[arrowPosition]
          )}
          style={{
            backgroundColor: styles.tooltipBgColor || 'rgba(0, 0, 0, 0.8)',
            color: styles.tooltipTextColor || 'white',
          }}
        >
          {label}

          {/* Arrow indicator based on position */}
          <div
            className={cn(
              'absolute h-2 w-2 rotate-45',
              arrowPosition === 'top' &&
                'top-full left-1/2 -translate-x-1/2 -translate-y-1/2',
              arrowPosition === 'right' &&
                'top-1/2 right-full translate-x-1/2 -translate-y-1/2',
              arrowPosition === 'bottom' &&
                'bottom-full left-1/2 -translate-x-1/2 translate-y-1/2',
              arrowPosition === 'left' &&
                'top-1/2 left-full -translate-x-1/2 -translate-y-1/2'
            )}
            style={{
              backgroundColor: styles.tooltipBgColor || 'rgba(0, 0, 0, 0.8)',
            }}
          />
        </div>
      )}
    </div>
  )
}

interface InteractionPointsManagerProps {
  points: InteractionPoint[]
  onPointClick?: (index: number) => void
  containerWidth?: number
  containerHeight?: number
}

export const InteractionPointsManager: React.FC<
  InteractionPointsManagerProps
> = ({
  points,
  onPointClick,
  containerWidth = 1920, // Default width if not provided
  containerHeight = 1080, // Default height if not provided
}) => {
  return (
    <div className='absolute inset-0'>
      {points.map((point, index) => {
        // Convert absolute coordinates to percentages
        // const xPercent = (point.x / containerWidth) * 100;
        // const yPercent = (point.y / containerHeight) * 100;
        // const widthPercent = point.width ? (point.width / containerWidth) * 100 : 0;
        // const heightPercent = point.height ? (point.height / containerHeight) * 100 : 0;
        const xPercent = point.xPercent
        const yPercent = point.yPercent
        const widthPercent = point.widthPercent
        const heightPercent = point.heightPercent
        const distanceToLeft =
          (containerWidth * xPercent!) / 100 -
          (containerWidth * widthPercent!) / 200
        const distanceToTop =
          (containerHeight * yPercent!) / 100 -
          (containerHeight * heightPercent!) / 200
        const distanceToRight =
          containerWidth -
          (distanceToLeft + (containerWidth * widthPercent!) / 200)
        const distanceToBottom =
          containerHeight -
          (distanceToTop + (containerHeight * heightPercent!) / 200)

        const width = (containerWidth * widthPercent!) / 100
        const height = (containerHeight * heightPercent!) / 100

        // 获取最近的边
        function getNearestEdge({
          distanceToLeft,
          distanceToTop,
          distanceToRight,
          distanceToBottom,
        }: {
          distanceToLeft: number
          distanceToTop: number
          distanceToRight: number
          distanceToBottom: number
        }): 'left' | 'top' | 'right' | 'bottom' {
          const distances = {
            left: distanceToLeft,
            top: distanceToTop,
            right: distanceToRight,
            bottom: distanceToBottom,
          }
          return Object.entries(distances).reduce((min, curr) =>
            curr[1] < min[1] ? curr : min
          )[0] as 'left' | 'top' | 'right' | 'bottom'
        }

        const nearestEdge = getNearestEdge({
          distanceToLeft,
          distanceToTop,
          distanceToRight,
          distanceToBottom,
        })

        return (
          <div key={point.id || `point-${index}`}>
            {/*  <div
              key={point.id}
              className={cn(
                'group/point absolute',
                'flex items-center justify-center',
                'rounded-md'
              )}
              style={{
                left: `${xPercent}% `,
                top: `${yPercent}%`,
                width: `calc(${widthPercent}% + 6px)`,
                height: `calc(${heightPercent}% + 6px)`,
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(142, 81, 255, 0.3)',
              }}
              onClick={() => onPointClick?.(index)}
            >
             
            //   <div className='relative h-[100%] w-[100%]'>
            //   {widthPercent! < 50 && heightPercent! < 50 && (
            //     <div
            //       className={cn(
            //         'absolute h-[40px] w-[40px]',
            //         nearestEdge === 'top' &&
            //           '-bottom-[46px] left-1/2 -translate-x-1/2',
            //         nearestEdge === 'bottom' &&
            //           '-top-[46px] left-1/2 -translate-x-1/2 rotate-180',
            //         nearestEdge === 'left' &&
            //           `top-1/2 -right-[46px] -translate-y-1/2 -rotate-90`,
            //         nearestEdge === 'right' &&
            //           'top-1/2 -left-[46px] -translate-y-1/2 rotate-90'
            //       )}
            //       style={{
            //         top:
            //           (nearestEdge === 'left' || nearestEdge === 'right') &&
            //           height < 40
            //             ? `-${height / 2}px !important`
            //             : '',
            //         left:
            //           (nearestEdge === 'top' || nearestEdge === 'bottom') &&
            //           width < 40
            //             ? `-${width / 2}px !important`
            //             : '',
            //       }}
            //     >
            //       <img
            //         src='/arrow.svg'
            //         alt='Interaction point'
            //         className='h-[40px] w-[40px]'
            //       />
            //     </div>
            //   )}
            // </div> 
          
            </div>  */}
            <div
              className='absolute size-5'
              style={{
                left: `${point.xPercent}%`,
                top: `${point.yPercent}%`,
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(142, 81, 255, 0.15)',
                border: `3px solid rgb(142, 81, 255)`,
                borderRadius: '50%',
                boxShadow: `0 0 0 20px #8e51ff4d`,
                zIndex: 2,
                pointerEvents: 'none',
              }}
            />
          </div>
        )
      })}
    </div>
  )
}
